<script setup>
import { computed, ref, watch } from 'vue'
import { Icon } from '@iconify/vue'
import AppButton from '@/components/atoms/AppButton'
import { useCarousel } from '@/hooks/useCarousel'

/**
 * AppCarousel — a horizontal, snap-scrolling row of equal cards, paged by arrows.
 * The anatomy follows Nuxt UI's Carousel (items, prev / next), trimmed to what the designs use.
 *
 * Arrows:
 *   header  — outlined arrows beside the title, dimmed at the ends (Home's "Get started" row)
 *   overlay — round translucent arrows over the track, shown on hover or keyboard focus, gone at the ends (the Help Hub's row)
 *   Neither shows arrows while every card already fits the box.
 *
 * `to` / `href` make the title of the header style a link with a chevron: a route, or an outside
 * page in a new tab (Home's "Insights ›" opens the Insights page).
 *
 * The default slot holds the cards; each sets its own width. `gap` is the Tailwind gap class
 * between them. `bleed` (px) runs the track past its box on both sides, so a card peeks in at the
 * edge of a padded panel. The track keeps a 2px inset so a card's glow is not clipped.
 *
 * An arrow that reaches its end is dimmed or removed; keyboard focus on it moves to the other
 * arrow, or to the track, never to the page.
 */
const props = defineProps({
  title:  { type: String, default: '' },                 // heading of the header style; `#title` replaces it
  arrows: { type: String, default: 'header', validator: (arrows) => ['header', 'overlay'].includes(arrows) },
  gap:    { type: String, default: 'gap-x-3' },          // Tailwind gap class between the cards
  bleed:  { type: Number, default: 0 },                  // px the track runs past its box on both sides
  to:     { type: [String, Object], default: null },     // route the title links to
  href:   { type: String, default: null },               // outside page the title links to, in a new tab
})

const ARROWS = {
  header: [
    { key: 'prev', label: 'Previous', icon: 'heroicons-outline:chevron-left', direction: -1 },
    { key: 'next', label: 'Next', icon: 'heroicons-outline:chevron-right', direction: 1 },
  ],
  overlay: [
    { key: 'prev', label: 'Previous', icon: 'heroicons:arrow-left-16-solid', direction: -1, side: 'left-3' },
    { key: 'next', label: 'Next', icon: 'heroicons:arrow-right-16-solid', direction: 1, side: 'right-3' },
  ],
}

const track = ref(null)
const arrowEls = { prev: null, next: null }

const { arrivedState, overflowing, scrollByPage } = useCarousel(track)

const isOverlay = computed(() => props.arrows === 'overlay')
// The linked title: an outside page opens in a new tab, a route goes through RouterLink.
const titleLink = computed(() => {
  if (props.href) return { is: 'a', attrs: { href: props.href, target: '_blank', rel: 'noopener noreferrer' } }
  if (props.to) return { is: 'RouterLink', attrs: { to: props.to } }
  return null
})
const atEnd = computed(() => ({ prev: arrivedState.left, next: arrivedState.right }))
// No arrows while every card fits; header arrows stay and dim at the ends, overlay arrows only show while there is more to see.
const arrows = computed(() => {
  if (!overflowing.value) return []
  return ARROWS[props.arrows].filter((arrow) => !isOverlay.value || !atEnd.value[arrow.key])
})
// The 2px inset keeps a card's glow inside the scroller's clip; a bleed is taken off the box, not the track.
const inset = computed(() => Math.max(props.bleed, 2))
const trackStyle = computed(() => ({
  paddingInline: `${inset.value}px`,
  scrollPaddingInline: `${inset.value}px`,
  marginInline: `-${inset.value - props.bleed}px`,
}))
const boxStyle = computed(() => (props.bleed ? { marginInline: `-${props.bleed}px` } : undefined))

const setArrowEl = (key) => (component) => {
  arrowEls[key] = component?.$el ?? null
}

// Runs before the DOM updates, while the arrow that reached its end still holds the focus.
watch([() => arrivedState.left, () => arrivedState.right], ([left, right], [wasLeft, wasRight]) => {
  const ended = (left && !wasLeft && 'prev') || (right && !wasRight && 'next')
  if (!ended || document.activeElement !== arrowEls[ended]) return

  const other = ended === 'prev' ? 'next' : 'prev'
  const target = atEnd.value[other] ? track.value : arrowEls[other]
  target?.focus()
})
</script>

<template>
  <section class="flex flex-col gap-y-4" role="region" aria-roledescription="carousel" :aria-label="title || undefined">
    <header v-if="!isOverlay" class="flex items-center justify-between gap-x-4">
      <slot name="title">
        <h2 class="text-lg font-semibold text-gray-900 touch:max-xl:text-base">
          <component :is="titleLink.is" v-if="titleLink" v-bind="titleLink.attrs" class="inline-flex items-center gap-x-2 hover:text-tint-1">
            {{ title }}
            <!-- Figma draws this 16px chevron with a 2px stroke, like the arrows. -->
            <Icon icon="heroicons-outline:chevron-right" class="size-4 shrink-0 [&>path]:stroke-[3px]" />
          </component>
          <template v-else>{{ title }}</template>
        </h2>
      </slot>

      <div v-if="arrows.length" class="flex items-center gap-x-2">
        <AppButton
          v-for="arrow in arrows"
          :key="arrow.key"
          :ref="setArrowEl(arrow.key)"
          variant="outlined"
          size="sm"
          :aria-label="arrow.label"
          :disabled="atEnd[arrow.key]"
          @click="scrollByPage(arrow.direction)"
        >
          <!-- Figma draws this 16px chevron with a 2px stroke. -->
          <Icon :icon="arrow.icon" class="size-4 shrink-0 [&>path]:stroke-[3px]" :class="{ 'text-gray-400': atEnd[arrow.key] }" />
        </AppButton>
      </div>
    </header>

    <div class="group/carousel relative" :style="boxStyle">
      <div
        ref="track"
        tabindex="-1"
        class="-my-0.5 flex snap-x snap-mandatory overflow-x-auto py-0.5 focus:outline-none child:shrink-0 child:snap-start [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        :class="gap"
        :style="trackStyle"
      >
        <slot />
      </div>

      <!-- Shown while the row is hovered or holds keyboard focus; touch has no hover, so there they always show. -->
      <template v-if="isOverlay">
        <AppButton
          v-for="arrow in arrows"
          :key="arrow.key"
          :ref="setArrowEl(arrow.key)"
          variant="overlay"
          rounded="full"
          class="absolute top-1/2 !size-12 -translate-y-1/2 shadow-md no-touch:opacity-0 group-hover/carousel:opacity-100 group-has-[:focus-visible]/carousel:opacity-100"
          :class="arrow.side"
          :aria-label="arrow.label"
          @click="scrollByPage(arrow.direction)"
        >
          <Icon :icon="arrow.icon" class="size-5 shrink-0" />
        </AppButton>
      </template>
    </div>
  </section>
</template>
